import { useState, useRef, useEffect, Suspense } from "react";
import { lazy } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./page1.module.css";
import { useVoiceAgent } from '../../../pages/VoiceAgentPage/context/VoiceAgentContext';
import { START_VOICE_CONVERSATION_STATE_KEY } from '../../../hooks/useEnquiry';
import VinAnimation from '../../../pages/VoiceAgentPage/components/VinAnimation';

const VoiceAgentPage = lazy(() => import('../../../pages/VoiceAgentPage/VoiceAgentPage'));
import ConnectingDots from '../../../assets/newDesktopView/connectingDots.png'

const SCHOOL_LOGOS = [
    { src: '/assets/school-logo-1.png', alt: 'The Heritage School' },
    { src: '/assets/school-logo-2.png', alt: 'Suchitra Academy' },
    { src: '/assets/school-logo-4.png', alt: 'The Shri Ram Universal School' },
    { src: '/assets/school-logo-5.png', alt: 'Meridian School, Kukatpally' },
    { src: '/assets/school-logo-6.png', alt: 'Lilawati Vidya Mandir School' },
    { src: '/assets/school-logo-7.png', alt: 'NGIS' },
    { src: '/assets/school-logo-8.png', alt: 'Accord School' },
    { src: '/assets/school-logo-10.png', alt: 'Hayde Heritage Academy, Kotdwara' },
    { src: '/assets/school-logo-11.png', alt: 'Laurel High Global School' },
    { src: '/assets/school-logo-12.png', alt: "Mother Mary's School, Mayur Vihar" },
    { src: '/assets/school-logo-14.png', alt: 'Bellwether International School' },
    { src: '/assets/school-logo-15.png', alt: "Balaji's Highfields International School" },
    { src: '/assets/school-logo-16.png', alt: 'CGR International School' },
    { src: '/assets/school-logo-17.png', alt: 'Delhi Public School, Varanasi' },
    { src: '/assets/school-logo-18.png', alt: 'Little Woods School' },
    { src: '/assets/school-logo-19.png', alt: 'Meena Bhujbal School of Excellence' },
    { src: '/assets/school-logo-20.png', alt: 'Magadham International School' },
    { src: '/assets/school-logo-21.png', alt: 'Magadham Vidyapeeth' },
    { src: '/assets/school-logo-22.png', alt: 'Mahavir Senior Model School' },
    { src: '/assets/school-logo-23.png', alt: 'Nopany High School' },
    { src: '/assets/school-logo-24.png', alt: 'Ortus International School' },
    { src: '/assets/school-logo-25.png', alt: 'Shri Harkrishan Sahib Public High School' },
    { src: '/assets/school-logo-26.png', alt: 'Silver Oaks International School, Oakhills' },
    { src: '/assets/school-logo-27.png', alt: 'Sr. Capital Sr. Sec. School' },
    { src: '/assets/school-logo-28.png', alt: 'STEM School, Vidya Nagar' },
    { src: '/assets/school-logo-29.png', alt: 'Shri Harkrishan Sahib Public Sr. Sec. School' },
    { src: '/assets/school-logo-30.png', alt: 'Vista International School' },
    { src: '/assets/school-logo-31.png', alt: 'WoodRock World School' },
    { src: '/assets/school-logo-32.png', alt: 'Westberry School, Tenali' },
    { src: '/assets/school-logo-33.png', alt: 'Horizon Valley Future School' },
];

const HINT_TEXTS = [
    'What is a future ready school?',
    'How can Coschool help my School?',
    'How does it improve class averages?',
];

export default function Page1({ handleBtnClick }) {
    const location = useLocation();
    const navigate = useNavigate();
    const logoSet = [...SCHOOL_LOGOS, ...SCHOOL_LOGOS];
    const startBtnRef = useRef(null);
    const vinCharacterRef = useRef(null);

    const [hintIndex, setHintIndex] = useState(0);
    const [hintVisible, setHintVisible] = useState(true);
    const [showVoiceAgent, setShowVoiceAgent] = useState(false);
    const [carouselPaused, setCarouselPaused] = useState(false);
    const { initiateConv, currentStep } = useVoiceAgent();

    const handleTalkToVin = () => {
        setShowVoiceAgent(true);
        initiateConv(true);
    };

    useEffect(() => {
        if (showVoiceAgent && currentStep === 0) {
            setShowVoiceAgent(false);
        }
    }, [currentStep, showVoiceAgent]);

    useEffect(() => {
        const handler = () => handleTalkToVin();
        window.addEventListener('startVoiceConversation', handler);
        return () => window.removeEventListener('startVoiceConversation', handler);
    }, []);

    useEffect(() => {
        if (location.state?.[START_VOICE_CONVERSATION_STATE_KEY]) {
            handleTalkToVin();
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setHintVisible(false);
            setTimeout(() => {
                setHintIndex((prev) => (prev + 1) % HINT_TEXTS.length);
                setHintVisible(true);
            }, 300);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    if (showVoiceAgent) {
        return (
            <Suspense fallback={<div>Loading...</div>}>
                <VoiceAgentPage />
            </Suspense>
        );
    }

    return (
        <div className={styles.page1} style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='64' height='64' viewBox='0 0 64 64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='31' y='31' width='2' height='2' fill='%2362A135'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '64px 64px' }}>
            <div className={styles.circleShadeTopRight} aria-hidden="true" />
            <div className={styles.circleShadeLeft} aria-hidden="true" />
            <div className={styles.circleShadeRight} aria-hidden="true" />
            <div className={styles.contentWrapper}>
                <div className={styles.page1Container}>
                    <div className={styles.mainheading}>
                        India’s trusted AI Partner for<br />
                        <span className={styles.mainheadingAccent}>future-ready schools.</span>
                    </div>
                    <div className={styles.subheading}>
                        Enabling schools across India to become <br /> AI-powered
                    </div>

                    {/* School logo carousel */}
                    <div
                        className={styles.carouselWrapper}
                        aria-label="Partner schools"
                        // Hover is mouse-only: touch fires emulated mouse events that would
                        // otherwise leave the marquee stuck paused after a tap
                        onPointerEnter={(e) => e.pointerType === 'mouse' && setCarouselPaused(true)}
                        onPointerLeave={(e) => e.pointerType === 'mouse' && setCarouselPaused(false)}
                        onTouchStart={() => setCarouselPaused(true)}
                        onTouchEnd={() => setCarouselPaused(false)}
                        onTouchCancel={() => setCarouselPaused(false)}
                    >
                        <div
                            className={`${styles.carouselTrack} ${carouselPaused ? styles.carouselPaused : ''}`}
                        >
                            {logoSet.map((logo, i) => (
                                <div
                                    key={i}
                                    className={styles.logoSlot}
                                    aria-hidden={i >= SCHOOL_LOGOS.length}
                                    onContextMenu={(e) => e.preventDefault()}
                                >
                                    <img
                                        src={logo.src}
                                        alt={i < SCHOOL_LOGOS.length ? logo.alt : ''}
                                        width="140"
                                        height="140"
                                        loading="eager"
                                        decoding="sync"
                                        draggable="false"
                                        className={styles.schoolLogo}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Bottom section: connector + prompt + CTA ── */}
                    <div className={styles.mediaCol}>
                        <div className={styles.connectorArea}>
                            {/* Connector line */}
                            <div className={styles.connector} aria-hidden="true" />

                            {/* CTA button with Vin character */}
                            <div className={styles.ctaGroup}>
                                <div className={styles.vinCharacter} ref={vinCharacterRef}>
                                    <VinAnimation className={styles.vinImg} />
                                </div>
                                <button
                                    ref={startBtnRef}
                                    className={styles.startBtn}
                                    type="button"
                                    onClick={() => handleBtnClick?.(startBtnRef.current, 'Start Conversation', vinCharacterRef.current)}
                                >
                                    <span className={styles.startBtnLabel}>Start Conversation</span>
                                    <img src={ConnectingDots} alt="" className={styles.connectingDots} aria-hidden="true" />
                                </button>
                            </div>
                            {/* Rotating hint text */}
                            <p className={`${styles.hintText} ${hintVisible ? styles.hintVisible : styles.hintHidden}`}>
                                &ldquo; {HINT_TEXTS[hintIndex]} &rdquo;
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
